import { destroySDKScript, getScriptID, type ReactPayPalScriptOptions } from '@paypal/react-paypal-js'

export type PayPalScriptOptions = ReactPayPalScriptOptions

// Long enough to survive a quick step navigation (e.g. booking form <-> payment step) or a
// React Strict Mode mount/unmount/mount cycle, short enough to still release memory once the
// booking flow is actually left.
const PENDING_TEARDOWN_DELAY_MS = 500

const pendingTeardownTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
const activeMountCounts = new Map<string, number>()

export function getPayPalScriptId(options: PayPalScriptOptions): string {
    return getScriptID(options)
}

/**
 * Marks a PayPal SDK script as in-use and cancels any teardown that was scheduled for it. Call
 * this when a component that renders `PayPalScriptProvider` for these options mounts.
 */
export function registerPayPalSdkMount(options: PayPalScriptOptions): void {
    const scriptId = getScriptID(options)

    const pendingTeardown = pendingTeardownTimeouts.get(scriptId)
    if (pendingTeardown) {
        clearTimeout(pendingTeardown)
        pendingTeardownTimeouts.delete(scriptId)
    }

    activeMountCounts.set(scriptId, (activeMountCounts.get(scriptId) ?? 0) + 1)
}

/**
 * Schedules a delayed teardown of the PayPal SDK script for these options. If a new mount for
 * the same script id registers before the delay elapses, the teardown is skipped so the already
 * bootstrapped script keeps loading in the background instead of triggering a second bootstrap
 * (which throws "Request listener already exists" on rapid unmount/remount).
 */
export function scheduleTeardownPayPalSdk(options: PayPalScriptOptions): void {
    const scriptId = getScriptID(options)
    const remainingMounts = Math.max((activeMountCounts.get(scriptId) ?? 1) - 1, 0)
    activeMountCounts.set(scriptId, remainingMounts)

    const timeoutId = setTimeout(() => {
        pendingTeardownTimeouts.delete(scriptId)
        if ((activeMountCounts.get(scriptId) ?? 0) > 0) {
            return
        }
        teardownPayPalSdk(options)
    }, PENDING_TEARDOWN_DELAY_MS)

    pendingTeardownTimeouts.set(scriptId, timeoutId)
}

export function teardownPayPalSdk(options: PayPalScriptOptions): void {
    destroySDKScript(getScriptID(options))

    const namespace = typeof options.dataNamespace === 'string' ? options.dataNamespace : 'paypal'
    const windowRecord = window as unknown as Record<string, unknown>

    try {
        delete windowRecord[namespace]
    } catch {
        windowRecord[namespace] = undefined
    }
}
